import { FC, MouseEvent, useState } from "react";
import {
  Box,
  createStyles,
  FormControl,
  FormControlLabel,
  FormLabel,
  makeStyles,
  Radio,
  RadioGroup,
  Typography,
} from "@material-ui/core";

import { AccountInfo, Button } from "components";
import { Option } from "components/FormHelpers";
import theme from "theme";

const useStyles = makeStyles(() =>
  createStyles({
    formControl: {
      width: "100%",
    },
    subaccountsContainer: {
      borderTop: `1px solid ${theme.custom.palette.gray.main}`,
      borderBottom: `1px solid ${theme.custom.palette.gray.main}`,
      marginLeft: 0,
      maxHeight: "400px",
      minHeight: "300px",
      overflow: "auto",
    },
  }),
);

export type SubaccountOption = {
  value: string;
  display: string;
};

interface SubaccountSelectionProps {
  username: string;
  royaltyPayorName: string;
  subaccounts: SubaccountOption[];
  onConnect: (option: Option) => Promise<void>;
}

const SubaccountSelection: FC<SubaccountSelectionProps> = ({
  username,
  royaltyPayorName,
  subaccounts,
  onConnect,
}) => {
  const classes = useStyles();
  const [selectedSubaccount, setSelectedSubaccount] = useState<Option | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  return (
    <>
      <AccountInfo
        royaltyPayorDisplayName={royaltyPayorName}
        username={username}
        subtitle="Credentials Verified"
      />
      <Box my={2}>
        <FormControl component="fieldset" className={classes.formControl}>
          <FormLabel>
            <Typography color="primary" variant="h6">
              Select Sub-Account
            </Typography>
          </FormLabel>
          <Box ml={2} className={classes.subaccountsContainer}>
            <RadioGroup
              data-attr="connect-royalty-payor-subaccount-choices"
              value={selectedSubaccount?.value ?? ""}
              name="subaccounts"
            >
              {subaccounts.map(subaccount => (
                <FormControlLabel
                  key={subaccount.value}
                  value={subaccount.value}
                  control={<Radio />}
                  label={
                    <Typography color="primary">
                      {subaccount.display}
                    </Typography>
                  }
                  onClick={() => setSelectedSubaccount(subaccount)}
                />
              ))}
            </RadioGroup>
          </Box>
        </FormControl>
      </Box>
      <Box display="flex" flexDirection="row-reverse" width="100%">
        <Button
          data-attr="connect-royalty-payor-subaccount-submit-button"
          customColor="green"
          variant="contained"
          disabled={!selectedSubaccount || isLoading}
          onClick={async (e: MouseEvent) => {
            e.preventDefault();
            setIsLoading(true);
            await onConnect(selectedSubaccount);
          }}
          isLoading={isLoading}
          style={{
            width: "115px",
          }}
        >
          {isLoading ? "Verifying" : "Next"}
        </Button>
      </Box>
    </>
  );
};

export default SubaccountSelection;
